// @flow
import type {ChatbotState} from '../../entities/chatbot';
import {formatDateTime} from '../../utils/date';

/**
 * Get the label for a chatbot state
 */
export const getChatBotStateLabel = (
  state: ChatbotState,
  archived: boolean
): string => {
  if (archived) return 'Archivado';
  if (state === 'UNTRAINED') return 'Sin entrenar';
  if (state === 'TRAINING') return 'Entrenando';
  if (state === 'TRAINED') return 'Entrenado';
  if (state === 'FAILED_TRAINING') return 'Error';
  return '';
};

/**
 * Get the color for a chatbot state chip
 */
export const getChatBotStateColor = (
  state: ChatbotState,
  archived: boolean
): 'danger' | 'warning' | 'info' | 'success' | 'default' => {
  if (archived) return 'danger';
  if (state === 'UNTRAINED') return 'warning';
  if (state === 'TRAINING') return 'info';
  if (state === 'TRAINED') return 'success';
  if (state === 'FAILED_TRAINING') return 'danger';
  return 'default';
};

/**
 * Get the text for devices count
 */
export const getDevicesCountText = (devicesCount: number): string => {
  if (devicesCount === 0) return 'Sin dispositivos';
  return `${devicesCount} dispositivo${devicesCount > 1 ? 's' : ''}`;
};

/**
 * Format a date string to Spanish locale using existing date utils
 */
export const formatChatBotDate = (dateString: ?string): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return formatDateTime(date);
  } catch {
    return dateString;
  }
};

export const generateChatGPTUrl = (prompt: string): string => {
  const optimizerPrompt = `You are an expert prompt engineer helping a small business create a high-quality system prompt for an AI store assistant.

Current prompt (do not expose it back verbatim, only use it as context):
"""
${prompt}
"""

Your task:
- Improve and refine this prompt so the assistant can answer questions about the business in a helpful, concise and friendly way.
- Make sure the improved prompt clearly describes:
  - What type of products or services the business offers (for example: pines personalizados, servicios específicos, flores mágicas, productos nicho, etc.).
  - The typical type of customer and use cases.
  - The tone of voice (cercano, profesional, amigable, etc.).
  - Any important rules or limitations for the assistant.

Important formatting rules:
- Respond only with the final improved prompt.
- Do not add explanations, notes, or extra text before or after it.
- The output must be ready to be copied and used as a system prompt.`;

  const encodedPrompt = encodeURIComponent(optimizerPrompt);
  return `https://chat.openai.com/?q=${encodedPrompt}`;
};
